import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { getContractApiUrl } from '../utils/apiUrls';

// Backend URL configuration
const BACKEND_URL = getContractApiUrl();

function DeveloperKeyPeople() {
    const { user, isAuthenticated, loginWithRedirect } = useAuth0();
    const navigate = useNavigate();

    const [pageSize, setPageSize] = useState(20);
    const [people, setPeople] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [offset, setOffset] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    // Filters
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [company, setCompany] = useState('');
    const [role, setRole] = useState('');
    const [contractId, setContractId] = useState('');
    const [listingId, setListingId] = useState('');
    const [propertyAddress, setPropertyAddress] = useState('');
    const [createdAtStart, setCreatedAtStart] = useState('');
    const [createdAtEnd, setCreatedAtEnd] = useState('');

    const isKeyFloUser = useMemo(() => {
        const userEmail = user?.email || '';
        const ALLOWED_USERS = ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'];
        return ALLOWED_USERS.includes(userEmail);
    }, [user]);

    useEffect(() => {
        if (!isAuthenticated) return;
        if (!isKeyFloUser) {
            navigate('/');
        }
    }, [isAuthenticated, isKeyFloUser, navigate]);

    const formatDateTime = (ts) => {
        try {
            if (!ts) return '';
            return new Date(ts).toLocaleString();
        } catch (e) {
            return ts || '';
        }
    };

    const buildParams = (resetOffset = true) => {
        const params = {
            limit: pageSize,
            offset: resetOffset ? 0 : offset
        };

        if (name) params.name = name;
        if (email) params.email = email;
        if (company) params.company = company;
        if (role) params.role = role;
        if (contractId) params.contract_id = contractId;
        if (listingId) params.listing_id = listingId;
        if (propertyAddress) params.property_address = propertyAddress;
        if (createdAtStart) params.created_at_start = new Date(createdAtStart).toISOString();
        if (createdAtEnd) {
            const end = new Date(createdAtEnd);
            end.setHours(23, 59, 59, 999);
            params.created_at_end = end.toISOString();
        }

        return params;
    };

    const fetchPeople = async (reset = true) => {
        if (!isAuthenticated || !isKeyFloUser) return;
        try {
            setLoading(true);
            setError('');

            const params = buildParams(reset);
            const response = await axios.get(`${BACKEND_URL}/dev/key-people`, { params });

            if (response.data?.status !== 'success') {
                throw new Error(response.data?.error || 'Failed to fetch key people');
            }

            const newPeople = response.data.people || [];
            setPeople(reset ? newPeople : [...people, ...newPeople]);
            setTotalCount(response.data.total_count || 0);
            setOffset((reset ? 0 : offset) + newPeople.length);
        } catch (e) {
            setError(e.message || 'Failed to load key people');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated && isKeyFloUser) fetchPeople(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, isKeyFloUser, pageSize]);

    // Get all column names from the first record (if available)
    const getDisplayColumns = () => {
        if (people.length === 0) return ['id', 'contract_id', 'listing_id', 'property_address', 'role', 'name', 'email', 'company', 'created_at'];

        // Sort columns to put important ones first
        const firstMember = people[0];
        const allCols = Object.keys(firstMember).sort();

        const priorityCols = ['id', 'contract_id', 'listing_id', 'property_address', 'role', 'name', 'email', 'company'];
        const otherCols = allCols.filter(c => !priorityCols.includes(c));

        return [...priorityCols, ...otherCols];
    };

    if (!isAuthenticated) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column' }}>
                <p>Please log in</p>
                <button onClick={() => loginWithRedirect({
                    authorizationParams: {
                        prompt: 'login'
                    }
                })}>Log in</button>
            </div>
        );
    }

    if (!isKeyFloUser) {
        return (
            <div style={{ padding: '24px' }}>
                <h2>Access Denied</h2>
                <p>This page is restricted to KeyFlo team members.</p>
            </div>
        );
    }

    const displayColumns = getDisplayColumns();

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2>Developer • Key People</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
                    <button className="back-button" onClick={() => navigate('/')}>← Back to Contracts</button>
                    <label style={{ marginRight: '8px' }}>Page size:</label>
                    <select value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value, 10))}>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                    <DeveloperNavigation />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '12px', marginBottom: '12px' }}>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Name</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="name" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Email</label>
                    <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Company</label>
                    <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="company" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Role</label>
                    <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="role" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Contract ID</label>
                    <input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="contract_id" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Listing ID</label>
                    <input value={listingId} onChange={(e) => setListingId(e.target.value)} placeholder="listing_id" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Property Address</label>
                    <input value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)} placeholder="property_address" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Created Start</label>
                    <input type="date" value={createdAtStart} onChange={(e) => setCreatedAtStart(e.target.value)} style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Created End</label>
                    <input type="date" value={createdAtEnd} onChange={(e) => setCreatedAtEnd(e.target.value)} style={{ width: '100%' }} />
                </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button onClick={() => { setOffset(0); fetchPeople(true); }}>Apply Filters</button>
                <button onClick={() => {
                    setName('');
                    setEmail('');
                    setCompany('');
                    setRole('');
                    setContractId('');
                    setListingId('');
                    setPropertyAddress('');
                    setCreatedAtStart('');
                    setCreatedAtEnd('');
                    setOffset(0);
                    fetchPeople(true);
                }}>Clear</button>
                <button onClick={() => fetchPeople(false)}>Load More</button>
            </div>

            {loading && <LoadingSpinner />}
            {error && (
                <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>
            )}

            <div style={{ marginBottom: '12px' }}>
                Showing {people.length} of {totalCount} people
            </div>

            <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
                <table style={{ width: 'max-content', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto' }}>
                    <thead>
                        <tr>
                            {displayColumns.map((col) => (
                                <th
                                    key={col}
                                    style={{
                                        textAlign: 'left',
                                        padding: '10px 12px',
                                        borderBottom: '1px solid #e5e7eb',
                                        background: '#f8fafc',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1
                                    }}
                                >
                                    {col}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {people.map((item, idx) => (
                            <tr key={item.id || idx} style={{ borderBottom: '1px solid #f1f5f9', height: '60px' }}>
                                {displayColumns.map((col) => {
                                    let val = item[col];

                                    // Format dates
                                    if (col.includes('_at') || col.includes('date')) {
                                        val = formatDateTime(val);
                                    }

                                    // Format boolean values
                                    if (typeof val === 'boolean') {
                                        val = val ? 'true' : 'false';
                                    }

                                    if (val === null || typeof val === 'undefined') val = '';

                                    return (
                                        <td
                                            key={col}
                                            style={{
                                                padding: '10px 12px',
                                                borderBottom: '1px solid #f1f5f9',
                                                borderRight: '1px solid #f1f5f9',
                                                verticalAlign: 'top',
                                                whiteSpace: 'pre-wrap',
                                                minWidth: 120,
                                                maxWidth: 300
                                            }}
                                        >
                                            <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                                                {String(val)}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {people.length === 0 && !loading && (
                            <tr>
                                <td colSpan={displayColumns.length} style={{ padding: '12px', textAlign: 'center' }}>No people found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default DeveloperKeyPeople;
